import {
    Button, ChangeEvent, Image, ImageType, Layout, LookupModelSelectionEvent, SlideoutDecorator, Snackbar, Switch,
    Toast
} from "@mcleod/components";
import { Api, ModelRow } from "@mcleod/core";
import { AutogenLayoutBillOfLading } from "./autogen/AutogenLayoutBillOfLading";

interface BillOfLadingProps {
    order_id: string,
    customer_id: string,
    _lookup_customer_id: ModelRow,
    shipperLocationId: string,
    bol_note: string,
    onError: (message: string) => Snackbar
}

export class BillOfLading extends AutogenLayoutBillOfLading {
    _props: any;
    _bol_report: string;
    _smc3_bol_report: string;
    _displayed_bol: string;

    static showBillOfLadingSlideout(props: BillOfLadingProps) {
        const layout = Layout.getLayout("lme/dispatch/BillOfLading") as BillOfLading;
        new SlideoutDecorator({
            layout,
            title: "Bill of Lading",
            fillVerticalSpace: true,
            layoutLoadListeners: async () => {
                await layout.mainDataSource.search({ order_id: props.order_id, bol_note: props.bol_note });
                if (layout.activeRow.get("error_message", false)) {
                    layout.slideOut().then(() => {
                        props.onError(layout.activeRow.get("error_message"));
                    });
                }

                delete props.onError;
                layout.activeRow.setLookupModelData("customer_id", props._lookup_customer_id);
                delete props._lookup_customer_id;

                layout.activeRow.set("order_id", props.order_id); // need to set it here for POST - seems that because order_id isn't a bound component it needs to be done this way
                layout.activeRow.setValues({ ...props });
                layout.setupContactListeners();
                const useSmc3GeneratedBol = layout.activeRow.get("use_smc3_generated_bol");
                const bolReport = layout.activeRow.get("bol_document", null);
                const smc3BolReport = layout.activeRow.get("smc3_bol_document");
                layout.registerPropsAndReports(props, bolReport, smc3BolReport);
                layout.panelSMC3BOL.visible = smc3BolReport != null;
                if (useSmc3GeneratedBol && layout.panelSMC3BOL.visible) {
                    layout.switchSMC3BOL.checked = true;
                } else {
                    await layout.generateBillOfLading(bolReport, props.order_id);
                }
            },
            addlComponents: layout.sendButton
        });
    }

    registerPropsAndReports(props: any, bolReport: string, smc3BolReport: string) {
        this._props = props; this._bol_report = bolReport; this._smc3_bol_report = smc3BolReport;
    }

    private sendButton: Button = new Button({
        dataSource: this.mainDataSource,
        busyWhenDataSourceBusy: true,
        caption: "Send",
        rowBreak: false,
        backgroundColor: "primary",
        color: "primary.reverse",
        borderWidth: 0,
        width: 150,
        onClick: () => this.sendEmail()
    });

    async switchSMC3BOLOnChange(event: ChangeEvent) {
        const smc3Switch = event.target as Switch;
        await this.generateBillOfLading(smc3Switch.checked ? this._smc3_bol_report : this._bol_report, this._props.order_id);
    }

    /** This is an event handler for the onChange event of switchCustomerShipper.  */
    switchCustomerShipperOnChange(event) {
        this.textboxCustomer.visible = !event.target.checked;
        this.textboxShipper.visible = event.target.checked;
        this.textboxContactName.clear();
        this.textboxEmail.clear();
    }

    private sendEmail() {
        if (this.validateSimple()) {
            const contactName: string = this.activeRow.get("contact_name");
            const toAddress: string = this.activeRow.get("to_address");
            const subject: string = this.activeRow.get("subject");
            Api.post("lme/dispatch/send-bill-of-lading",
                { order_id: this._props.order_id, subject: subject, contact_name: contactName, to_address: toAddress, pdf_attachment: this._displayed_bol }).then(() => {
                    this.slideOut().then(() => {
                        Toast.showSuccessToast("Bill of Lading Sent");
                    })
                });
        }
    }

    async generateBillOfLading(bolReport: string, orderId: string) {
        this._displayed_bol = bolReport;
        const imgBol = new Image({ imageType: ImageType.PDF, fillHeight: true, minWidth: 700, id: "imageBolReport" });
        imgBol.imageBytes = bolReport;
        this.panelPdf.removeAll();
        this.panelPdf.add(imgBol);
        this.activeRow.set("subject", "Bill of Lading for Order # " + orderId);
    }

    private setupContactListeners() {
        this.textboxCustomer.displayData(this.activeRow, null, 0);
        this.textboxContactName.addBeforeLookupModelSearchListener(event => {
            if (this.activeRow.get("lookup_type") === "S") {
                // Shipper
                if (!this.activeRow.isNull("shipperLocationId")) {
                    event.filter.parent_row_type = "L";
                    event.filter.parent_row_id = this.activeRow.get("shipperLocationId");
                    event.filter.is_active = "Y";
                } else {
                    event.preventDefault();
                }
            } else {
                // Customer
                event.filter.parent_row_id = this.activeRow.get("customer_id");
                event.filter.parent_row_type = "C";
            }
        });

        this.textboxContactName.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                const emailAddress = event.selection.get("email", null);
                if (emailAddress)
                    this.activeRow.set("to_address", emailAddress);
            }
        });
    }
}
